import * as _ from 'lodash'
import {
    ICell, ICellMetric, ICellMetricUOM, ICellMetricUOMConversions,
    ICubePerspective,
    ICubePerspectiveOptions,
    ICubeSource, ICubeSourceMetricChangeEvent,
    IDimensionCompositeKeys, IDimensionDistinctKeys, IMetricUOMConversion
} from "./PA.HyperRectangle.IF";
import {DimensionType, IHierarchy} from "../PA.Core/PA.Core.IF.Hierarchy";
import {CellUtils} from "./CellUtils";
import {PerspectiveLocalCell} from "./PerspectiveLocalCell";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {IMessageProvider, IProgressProvider} from "../PA.Core/PA.Core.IF.Basic";
import {IUOMPolicyOptions, UOM} from "../PA.MD.Services/PA.MD.Services.IF";
import {CellMetricUtils, ICellMetricCalcPass} from "./CellMetricUtils";
import {isPromise} from "../PA.Core/Basic/ASync";
import {Subscription} from "rxjs";
import {ExecutionSerializer} from "../PA.Core/Basic/ExecutionSerializer";

export interface IPerspectiveCreateOptions {
    cells: ICell[] | (() => Promise<ICell[]>),
    // UOM
    cacheCellMetricUOMs?: {[metricId:string]: ICellMetricUOM},
    uomPolicyOptions?: IUOMPolicyOptions
}

export class Perspective
  implements ICubePerspective
{
    private _name: string
    private _cells: PerspectiveLocalCell[]
    private _leafCells: ICell[]
    private _leafCellFunc: () => Promise<ICell[]>

    private _cubeSource: ICubeSource
    private _perspectiveOptions: ICubePerspectiveOptions
    private _bEnabled: boolean
    private _bHasLocalLeafCells: boolean

    private _arrDimensionTypes: DimensionType[]
    private _dctDimensions: {[dimensionTypeId: string]: IHierarchy }

    private _arrCellMetrics: ICellMetric[]
    private _dctCacheCellMetricUOM: {[metricId:string]: ICellMetricUOM}
    private _uomPolicyOptions: IUOMPolicyOptions
    private _dctCellMetricUOM: {[metricId:string]: ICellMetricUOM}
    private _dctCellMetricUOMConversions: {[metricId:string]: ICellMetricUOMConversions}

    private _arrCellMetricCalcPasses: ICellMetricCalcPass[]
    private _dctCalcPassMetrics: {[metricId: string]: string}
    private _bLoaded: boolean
    private _OnLoadComplete: Promise<void>
    private _subOnCubeSourceMetricChange: Subscription
    private _loadProgressProvider: ProgressProvider
    private _loadMessageProvider: MessageProvider
    private _dctQueuedMetricUpdates: {[metricId: string]: string}
    private qsProcessNotificationQueue: ExecutionSerializer

    //constructor for local source
    constructor(name: string,
                cubeSource: ICubeSource,
                perspectiveOptions: ICubePerspectiveOptions,
                options: IPerspectiveCreateOptions )
    {
        this._name = name
        this._cubeSource = cubeSource
        this._perspectiveOptions = perspectiveOptions || {MetricIds: [], Dimensions: {}}
        this._bHasLocalLeafCells = true
        this._bEnabled = true
        this._leafCells = undefined
        this._leafCellFunc = undefined
        if(_.isFunction(options.cells))
            this._leafCellFunc = options.cells
        else
            this._leafCells = options.cells
        this._dctCacheCellMetricUOM = options.cacheCellMetricUOMs || {}
        this._uomPolicyOptions = options.uomPolicyOptions || {}

        this._arrDimensionTypes = []
        this._dctDimensions = {}

        this._dctCellMetricUOM = {}
        this._dctCellMetricUOMConversions = {}
        this._arrCellMetrics = []
        this._arrCellMetricCalcPasses = []
        this._dctCalcPassMetrics = {}

        this._cells = []
        if(_.isBoolean(perspectiveOptions.bEnabled))
            this._bEnabled = perspectiveOptions.bEnabled

        this._bLoaded = false
        this._OnLoadComplete = undefined
        this._dctQueuedMetricUpdates = {}

        this._loadMessageProvider = new MessageProvider()
        this._loadProgressProvider = new ProgressProvider("Loading Perspective")
        this.qsProcessNotificationQueue = new ExecutionSerializer(this, this._Perspective_processNotificationQueue, [],500)

    }


    get CubePerspectiveName(): string { return this._name }

    get CubePerspectiveOptions(): ICubePerspectiveOptions { return this._perspectiveOptions }

    get CubeSource(): ICubeSource { return this._cubeSource }

    get LoadMessageProvider(): IMessageProvider { return this._loadMessageProvider }

    get LoadProgressProvider(): IProgressProvider { return this._loadProgressProvider }



    getDimensions(): DimensionType[] { return this._arrDimensionTypes }

    getDimensionHierarchy(dimensionType: DimensionType): IHierarchy { return this._dctDimensions[dimensionType]; }

    getCellMetrics(): ICellMetric[] { return this._arrCellMetrics; }

    getCellMetric(aMetricId: string): ICellMetric {
        return this.CubeSource.getMetric(aMetricId)
    }

    getCellMetricUOM(metricId: string): ICellMetricUOM {
        return this._dctCellMetricUOM[metricId];
    }

    getCellMetricConversions(metricId: string): ICellMetricUOMConversions {
        return this._dctCellMetricUOMConversions[metricId];
    }

    get Cells(): ICell[] { return this._cells }

    get bPerspectiveEnabled(): boolean { return this._bEnabled }

    get bCubePerspectiveLoaded(): boolean { return this._bLoaded }

    get OnCubePerspectiveLoadComplete(): Promise<void> {
        const self = this

        if (self.bCubePerspectiveLoaded)
            return Promise.resolve()

        if (!_.isEmpty(self._OnLoadComplete))
            return self._OnLoadComplete


        // Load Promise
        let returnValue = self._OnLoadComplete = (async () => {
                try {
                    if (_.isFunction(self._leafCellFunc))
                        self._leafCells = await self._leafCellFunc()

                    _.forEach(self._perspectiveOptions.Dimensions, (aFilterSpec, aDimType) => {
                        // @ts-ignore
                        let aDimHierarchy = self._cubeSource.getDimensionHierarchy(aDimType)
                        if (aFilterSpec.LevelId == "All")
                            return
                        // @ts-ignore
                        self._arrDimensionTypes.push(aDimType)
                        self._dctDimensions[aDimType] = aDimHierarchy
                    })

                    _.forEach(self._perspectiveOptions.MetricIds, (aMetricId) => {
                        let aCellMetric = self.CubeSource.getMetric(aMetricId)
                        if (aCellMetric)
                            self._arrCellMetrics.push(aCellMetric)
                    })

                    if (!self._bHasLocalLeafCells) {
                        //-- HR Server Service not implemented, yet
                        console.log("HR Server Service not implemented")
                        return
                    }

                    // Create the calc passes
                    self._arrCellMetricCalcPasses = CellMetricUtils.getCalcPasses(self._cubeSource, self.CubePerspectiveOptions.MetricIds)
                    _.forEach(self._arrCellMetricCalcPasses, (aPass) => {
                        _.forEach(aPass.Metrics, (aMetricId) => {
                            self._dctCalcPassMetrics[aMetricId] = aMetricId
                        })
                    })
                    // Set up the UOMs, Converters for Metrics with UOM
                    let dctPerspectiveConverters = self._perspectiveOptions?.Converters || {}

                    for (let aMetric of self._arrCellMetrics) {

                        let metricUOM = self._dctCacheCellMetricUOM[aMetric.MetricId]

                        if (!metricUOM && _.isFunction(aMetric.getMetricUOM)) {
                            // Get the UOM from the metric
                            let lookupUOM = aMetric.getMetricUOM(this._uomPolicyOptions)
                            if (isPromise(lookupUOM))
                                lookupUOM = await lookupUOM
                            metricUOM = lookupUOM
                        }

                        if (metricUOM)
                            self._dctCellMetricUOM[aMetric.MetricId] = metricUOM
                        else
                            continue

                        // Setup Any Converters
                        let bConvertUOM = _.isFunction(aMetric.getMetricUOMConverters) && !_.isEmpty(dctPerspectiveConverters)
                        if (!bConvertUOM)
                            continue

                        let cellConverters = aMetric.getMetricUOMConverters()
                        let allConverters = _.union(cellConverters?.NumeratorUOMConverters || [], cellConverters?.DenominatorUOMConverter || [])
                        bConvertUOM = _.filter(allConverters, (a) => a in dctPerspectiveConverters).length > 0
                        if (!bConvertUOM)
                            continue

                        let finalCellUOM = _.cloneDeep(metricUOM)

                        function createIMetricUOMConversions(uom: UOM, converterIds: string[]): IMetricUOMConversion[] {
                            if (_.isEmpty(uom) || _.isEmpty(converterIds))
                                return []
                            let currentUOM = uom
                            let rv: IMetricUOMConversion[] = []
                            for (let aCnvtrId of converterIds) {
                                let tgtCnvUOM = dctPerspectiveConverters[aCnvtrId]
                                if (!tgtCnvUOM)
                                    continue
                                let cnvtr = self._cubeSource.getMetricUOMConverter(aCnvtrId)
                                if (cnvtr.canConvert(currentUOM, tgtCnvUOM.UOM)) {
                                    rv.push({
                                        convertedId: aCnvtrId,
                                        converter: cnvtr,
                                        fromUOM: currentUOM,
                                        toUOM: tgtCnvUOM.UOM
                                    })
                                    currentUOM = tgtCnvUOM.UOM
                                }

                            } // Next Converter

                            return rv
                        }

                        let metricUOMConversions: ICellMetricUOMConversions = {
                            NumeratorConversions: createIMetricUOMConversions(finalCellUOM.NumeratorUOM, cellConverters.NumeratorUOMConverters),
                            DenominatorConversions: createIMetricUOMConversions(finalCellUOM.DenominatorUOM, cellConverters.DenominatorUOMConverter),
                        }

                        let bMetricConverted = false
                        // If Conversion are done, reset the UOM to the last
                        if (!_.isEmpty(metricUOMConversions.NumeratorConversions)) {
                            let lastCnvr = _.last(metricUOMConversions.NumeratorConversions)
                            finalCellUOM.NumeratorUOM = lastCnvr.toUOM
                            bMetricConverted = true
                        }

                        if (!_.isEmpty(metricUOMConversions.DenominatorConversions)) {
                            let lastCnvr = _.last(metricUOMConversions.DenominatorConversions)
                            finalCellUOM.DenominatorUOM = lastCnvr.toUOM
                            bMetricConverted = true
                        }

                        if (bMetricConverted) {
                            self._dctCellMetricUOM[aMetric.MetricId] = finalCellUOM
                            self._dctCellMetricUOMConversions[aMetric.MetricId] = metricUOMConversions
                        }
                    }

                    self._cells = []

                    // Create Perspective Local Cells
                    let dctCellsGroups: { [cellId: string]: { DimensionKeys: { [dimTypeId: string]: string }, LeafCells: ICell[] } } = {}
                    for (let aLeafCell of self._leafCells) {

                        let item = {
                            DimensionKeys: <string[]>[],
                            LeafCell: aLeafCell,
                            bError: false,
                            sErrors: <string[]>[]
                        }
                        let dimKeys: { [dimTypeId: string]: string } = {}

                        _.forEach(self._perspectiveOptions.Dimensions, (aFilterSpec, aDimType) => {
                            // @ts-ignore
                            let aDimHierarchy = self._cubeSource.getDimensionHierarchy(aDimType)
                            if (_.isEmpty(aDimHierarchy)) {
                                item.bError = true
                                item.sErrors.push(`Dimension Hierarchy not found, ${aDimType}`)
                                return
                            }

                            let cellKey = aLeafCell.getKey(aDimType) + '' // Ensure it's a string
                            let parentKey = ""

                            if (!_.isEmpty(aFilterSpec.LevelId)) {
                                let cellNode = aDimHierarchy.getNode(cellKey)
                                if (cellNode?.NodeLevel == aFilterSpec.LevelId) {
                                    parentKey = cellKey
                                } else {
                                    let parent = aDimHierarchy.getParents(cellKey, aFilterSpec.LevelId)
                                    parentKey = parent[cellKey]?.NodeId
                                }
                                if (_.isEmpty(parentKey))
                                    parentKey = "0"
                            } else
                                parentKey = "All"

                            dimKeys[aDimType] = parentKey
                            item.DimensionKeys.push(parentKey)
                        }) //-- Next Dimension

                        let cellId = ""
                        if (item.bError)
                            cellId = "Error"
                        else
                            cellId = item.DimensionKeys.join("-|-")

                        if (_.isEmpty(cellId))
                            cellId = "|Total|"

                        if (cellId in dctCellsGroups) {
                            dctCellsGroups[cellId].LeafCells.push(aLeafCell)
                        } else {
                            let cellGroup = {
                                DimensionKeys: dimKeys,
                                LeafCells: [aLeafCell]
                            }
                            dctCellsGroups[cellId] = cellGroup
                        }

                    } // Next Cell

                    _.forEach(dctCellsGroups, (group, cellId) => {

                        if (cellId == "Error") {
                            //-- Cell
                            //-- Discard the Error Cell

                        } else {
                            //-- Cell
                            let aPerspectiveCell
                                = new PerspectiveLocalCell(self._cubeSource,
                                self,
                                cellId,
                                group.DimensionKeys,
                                self.CubePerspectiveOptions.MetricIds,
                                group.LeafCells,
                                true,
                                self.bPerspectiveEnabled)
                            self._cells.push(aPerspectiveCell)
                        }

                    })

                    if (self._cubeSource.OnCubeSourceMetricChange) {
                        self._subOnCubeSourceMetricChange = self._cubeSource.OnCubeSourceMetricChange.subscribe(self.evtOnCubeSourceMetricChange.bind(self))
                    }

                    self._bLoaded = true

                } catch (e) {
                    throw e
                }
            }

        )();

        return returnValue
    }

    private evtOnCubeSourceMetricChange(evt: ICubeSourceMetricChangeEvent): void {
        const self = this

        //--
        if(_.isEmpty(evt))
            return

        //--
        if(evt.Perspective == this)
            return

        //-- Check if the metrics intersect with mine
        let bIntersects = false
        let arrMetrics = _.isString(evt.Metric)?[evt.Metric]: evt.Metric

        // This could be better, check the
        _.forEach(arrMetrics, (aMetricId) => {
            if(aMetricId in self._dctCalcPassMetrics){
                bIntersects = true
                return false
            }
        })

        if(!bIntersects)
            return

        console.log("Perspective Invalidated: evtOnCubeSourceMetricChange, refreshing")
        //-- Refresh cells
        _.forEach(self._cells, (aCell) => {
            aCell.refreshMetricValues([], true)
        })

    }

    _notifyCellUpdate(aCell: PerspectiveLocalCell, metrics: string[]){
        _.forEach(metrics, (a) => {
            if(!(a in this._dctQueuedMetricUpdates))
                this._dctQueuedMetricUpdates[a] = a
        })

        this.qsProcessNotificationQueue.Run({})
    }

    private _Perspective_processNotificationQueue(): Promise<void> {
        let self = this

        let arrMetricIds = _.values(self._dctQueuedMetricUpdates)
        self._dctQueuedMetricUpdates = {}
        if(_.isEmpty(arrMetricIds)){
            return Promise.resolve()
        }

        if(self._cubeSource.OnCubeSourceMetricChange){
            self._cubeSource.OnCubeSourceMetricChange.next({
                Source: self._cubeSource,
                Perspective: self,
                Metric: arrMetricIds
            })
        }

        return Promise.resolve()

    }

        destroy(): void {
        if(this._subOnCubeSourceMetricChange)
            this._subOnCubeSourceMetricChange.unsubscribe()
    }



    getDistinctKeys(arrDimensionTypes: DimensionType[]): IDimensionDistinctKeys {
        return CellUtils.getDistinctKeys(arrDimensionTypes, this._cells)
    }


    getCompositeKeys(arrDimensionTypes: DimensionType[]): IDimensionCompositeKeys {
        return CellUtils.getCompositeKeys(arrDimensionTypes, this._cells, false)
    }

    // Functions for PerspectiveLocalCell

    getMetricCalcPasses(): ICellMetricCalcPass[] {
        return this._arrCellMetricCalcPasses
    }


}